
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FiUploadCloud, FiX, FiDownload } from "react-icons/fi";
import { uploadParkAdminBulk } from "../redux/actions/ParkAdminBulkUploadActions";
import theme from "../components/shared/theme";
import { ClipLoader } from "react-spinners";

const UserParkAdminAddBulk = () => {
  const dispatch = useDispatch();

  const { loading, data, error } = useSelector(
    (state) => state.parkAdminBulkUpload
  );

  const [csvFile, setCsvFile] = useState(null);
  const [previewFileName, setPreviewFileName] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSkippedModal, setShowSkippedModal] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setCsvFile(file);
    setPreviewFileName(file?.name || "");
  };

  const handleUpload = () => {
    if (!csvFile) {
      alert("Please select a CSV/XLSX file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", csvFile);

    dispatch(uploadParkAdminBulk(formData)).then(() => {
      setShowSuccessModal(true);
    });
  };

  // Helper function to get summary data with backward compatibility
  const getSummaryData = () => {
    if (!data?.data) return null;
    
    const summary = data.data;
    
    // Handle both response formats
    const totalRecords = summary.total_records || 0;
    
    // For inserted records - check both possible structures
    let insertedRecords = 0;
    if (typeof summary.inserted_records === 'number') {
      insertedRecords = summary.inserted_records;
    } else if (summary.inserted_records && typeof summary.inserted_records === 'object') {
      insertedRecords = Object.keys(summary.inserted_records).length;
    } else if (summary.admin_inserted !== undefined) {
      insertedRecords = summary.admin_inserted;
    }
    
    // For skipped records - check both possible structures
    let skippedRecords = 0;
    if (typeof summary.skipped_records === 'number') {
      skippedRecords = summary.skipped_records;
    } else if (summary.skipped_records && typeof summary.skipped_records === 'object') {
      skippedRecords = Object.keys(summary.skipped_records).length;
    }
    
    // Get skipped records details
    const skippedDetails = summary.skipped_records_details || summary.skipped_phones || [];
    
    return {
      totalRecords,
      insertedRecords,
      skippedRecords,
      skippedDetails,
      hasSuccessfulUploads: insertedRecords > 0,
      hasSkippedRecords: skippedRecords > 0
    };
  };

  // Function to export skipped records to CSV
  const exportSkippedToCSV = () => {
    const summary = getSummaryData();
    if (!summary || summary.skippedDetails.length === 0) return;

    const headers = ["Row", "Project Code", "Reason"];
    const csvData = summary.skippedDetails.map(detail => [
      detail.row || "N/A",
      detail.project_code || detail || "N/A",
      detail.reason || "Duplicate project for admin"
    ]);

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(field => `"${field}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `skipped_records_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = getSummaryData();

  return (
    <div
      style={{
        backgroundColor: theme.colors.background,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: theme.spacing.lg,
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: theme.spacing.xl,
          borderRadius: theme.radius.lg,
          boxShadow: theme.shadow.md,
          maxWidth: 600,
          width: "100%",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: theme.font.size["2xl"],
            fontWeight: theme.font.weight.bold,
            marginBottom: theme.spacing.md,
            color: theme.colors.primary,
          }}
        >
          Multiple Park Admin Upload
        </h2>

        {/* Download Template Button */}
        <a
          href="https://e-attendance-ebs.s3.ap-south-1.amazonaws.com/uploads/6118e516-6a2a-46e4-827c-72772ecb2bb8"
          download
          style={{
            display: "inline-block",
            marginBottom: theme.spacing.md,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            backgroundColor: "#0d6efd",
            color: "white",
            borderRadius: theme.radius.md,
            textDecoration: "none",
            fontWeight: "600",
          }}
        >
          📥 Download Excel Template
        </a>

        {/* File Upload Box */}
        <label
          htmlFor="csv-upload"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            border: `2px dashed ${theme.colors.gray}`,
            borderRadius: theme.radius.md,
            padding: theme.spacing.lg,
            cursor: "pointer",
            backgroundColor: "#f9f9f9",
          }}
        >
          <FiUploadCloud size={48} color={theme.colors.grayDark} />
          <p style={{ marginTop: theme.spacing.sm, color: theme.colors.grayDark }}>
            Click to choose CSV or Excel file
          </p>
          <input
            id="csv-upload"
            type="file"
            accept=".csv, .xlsx, .xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
        </label>

        {previewFileName && (
          <p
            style={{
              marginTop: theme.spacing.sm,
              fontSize: theme.font.size.base,
              color: theme.colors.grayDark,
            }}
          >
            Selected: <strong>{previewFileName}</strong>
          </p>
        )}

        {/* Error Box */}
        {error && (
          <div
            style={{
              marginTop: theme.spacing.md,
              backgroundColor: "#ffebee",
              color: "#c62828",
              padding: theme.spacing.sm,
              borderRadius: theme.radius.md,
              fontWeight: "600",
            }}
          >
            ❌ {error}
          </div>
        )}

        {/* Upload Button */}
        <button
          onClick={handleUpload}
          disabled={loading}
          style={{
            marginTop: theme.spacing.md,
            padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
            backgroundColor: theme.colors.primary,
            color: "white",
            border: "none",
            borderRadius: theme.radius.md,
            fontSize: theme.font.size.base,
            cursor: loading ? "not-allowed" : "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {loading ? <ClipLoader size={18} color="#fff" /> : "Upload"}
        </button>

        {/* Upload Summary */}
        {summary && (
          <div
            style={{
              marginTop: theme.spacing.lg,
              textAlign: "left",
              backgroundColor: "#f7f7f7",
              borderRadius: theme.radius.md,
              padding: theme.spacing.md,
              fontSize: theme.font.size.base,
            }}
          >
            <h4
              style={{
                fontWeight: theme.font.weight.semibold,
                marginBottom: theme.spacing.sm,
              }}
            >
              Upload Summary
            </h4>
            
            {/* Success/Failure Indicator */}
            <div
              style={{
                padding: theme.spacing.sm,
                borderRadius: theme.radius.md,
                marginBottom: theme.spacing.md,
                backgroundColor: summary.hasSuccessfulUploads ? "#e8f5e8" : "#fff3cd",
                border: `1px solid ${summary.hasSuccessfulUploads ? "#d4edda" : "#ffeaa7"}`,
                color: summary.hasSuccessfulUploads ? "#155724" : "#856404",
              }}
            >
              {summary.hasSuccessfulUploads 
                ? `✅ Successfully uploaded ${summary.insertedRecords} records`
                : "⚠️ No new records were uploaded (all were duplicates)"
              }
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: theme.spacing.sm }}>
              <p><strong>Total Records:</strong> {summary.totalRecords}</p>
              <p><strong>Inserted Records:</strong> {summary.insertedRecords}</p>
              <p><strong>Skipped Records:</strong> {summary.skippedRecords}</p>
            </div>

            {summary.skippedDetails.length > 0 && (
              <div style={{ marginTop: theme.spacing.sm }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: theme.spacing.sm }}>
                  <p style={{ margin: 0 }}><strong>Skipped Records Details:</strong></p>
                  <button
                    onClick={exportSkippedToCSV}
                    style={{
                      padding: "4px 8px",
                      backgroundColor: "#28a745",
                      color: "white",
                      border: "none",
                      borderRadius: theme.radius.sm,
                      cursor: "pointer",
                      fontSize: theme.font.size.sm,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <FiDownload size={14} />
                    Export CSV
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    borderRadius: theme.radius.md,
                    border: "1px solid #e0e0e0",
                    marginTop: theme.spacing.sm,
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f5f5f5" }}>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>Row</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>Project Code</th>
                        <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #e0e0e0" }}>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.skippedDetails.slice(0, 5).map((detail, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <td style={{ padding: "8px" }}>{detail.row || i + 1}</td>
                          <td style={{ padding: "8px" }}>{detail.project_code || detail}</td>
                          <td style={{ padding: "8px", color: "#b00020" }}>{detail.reason || "Duplicate project for admin"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {summary.skippedDetails.length > 5 && (
                    <div 
                      style={{ 
                        padding: "12px", 
                        textAlign: "center", 
                        color: "#0d6efd",
                        cursor: "pointer",
                        fontWeight: "600",
                        backgroundColor: "#f8f9fa",
                        borderTop: "1px solid #e0e0e0"
                      }}
                      onClick={() => setShowSkippedModal(true)}
                    >
                      View all {summary.skippedDetails.length} skipped records
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Success Popup */}
      {showSuccessModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "100vw",
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "30px",
              borderRadius: "12px",
              width: "350px",
              textAlign: "center",
              boxShadow: theme.shadow.md,
            }}
          >
            <h3 style={{ color: summary?.hasSuccessfulUploads ? "#22bb33" : "#ffc107" }}>
              {summary?.hasSuccessfulUploads ? "✔ Upload Successful!" : "⚠ Upload Completed"}
            </h3>
            <p style={{ marginTop: "10px" }}>
              {summary?.hasSuccessfulUploads 
                ? `Park Admin bulk upload completed successfully. ${summary.insertedRecords} records were uploaded.`
                : "Upload completed. All records were duplicates and no new records were inserted."
              }
            </p>

            <button
              onClick={() => setShowSuccessModal(false)}
              style={{
                marginTop: "20px",
                padding: "10px 20px",
                backgroundColor: theme.colors.primary,
                color: "white",
                border: "none",
                borderRadius: theme.radius.md,
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Skipped Records Modal */}
      {showSkippedModal && summary && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: "100vw",
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
            padding: theme.spacing.lg,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              width: "90%",
              maxWidth: "800px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: theme.shadow.lg,
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: theme.spacing.lg,
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h3 style={{ margin: 0, color: "#b00020" }}>
                Skipped Records ({summary.skippedDetails.length})
              </h3>
              <div style={{ display: "flex", gap: theme.spacing.sm, alignItems: "center" }}>
                <button
                  onClick={exportSkippedToCSV}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#28a745",
                    color: "white",
                    border: "none",
                    borderRadius: theme.radius.sm,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: theme.font.size.sm,
                  }}
                >
                  <FiDownload size={14} />
                  Export CSV
                </button>
                <button
                  onClick={() => setShowSkippedModal(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "20px",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px",
                  }}
                >
                  <FiX />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                padding: theme.spacing.md,
              }}
            >
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: theme.radius.md,
                  border: "1px solid #e0e0e0",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead style={{ position: "sticky", top: 0, backgroundColor: "#f5f5f5", zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #e0e0e0", fontWeight: "600" }}>Row</th>
                      <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #e0e0e0", fontWeight: "600" }}>Project Code</th>
                      <th style={{ padding: "12px", textAlign: "left", borderBottom: "1px solid #e0e0e0", fontWeight: "600" }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.skippedDetails.map((detail, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "12px" }}>{detail.row || i + 1}</td>
                        <td style={{ padding: "12px" }}>{detail.project_code || detail}</td>
                        <td style={{ padding: "12px", color: "#b00020" }}>{detail.reason || "Duplicate project for admin"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: theme.spacing.md,
                borderTop: "1px solid #e0e0e0",
                textAlign: "center",
                backgroundColor: "#f8f9fa",
              }}
            >
              <button
                onClick={() => setShowSkippedModal(false)}
                style={{
                  padding: "8px 20px",
                  backgroundColor: theme.colors.primary,
                  color: "white",
                  border: "none",
                  borderRadius: theme.radius.md,
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserParkAdminAddBulk;